import React, { useState } from "react";
import {
  eyeclosed,
  eyeopened,
  verificationbadge,
} from "../../common/svg";
import CustomChip from "../Chip";
import { AuthInputTextProps } from "../../service/interfaces/Components";
import SecureImage from "../BucketImage/Image";

const AuthInputText: React.FC<AuthInputTextProps> = ({
  label,
  value,
  errors,
  type = "text",
  placeholder,
  autoComplete,
  name,
  disabled = false,
  onChange,
  onBlur,
  required = true,
  className = "",
  register,
  maxLength,
  verified,
  title = "copy not allowed",
  toUpperCase = false,
}) => {
  const [isTruncated, setIsTruncated] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const inputId = name || label.toLowerCase();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let newValue = e.target.value;
    if (toUpperCase) {
      newValue = newValue.toUpperCase();
    }

    if (type === "number" || type === "tel") {
      // Only keep digits
      newValue = newValue.replace(/\D/g, "");
    }

    // Apply maxLength constraint
    if (maxLength && newValue.length > maxLength) {
      setIsTruncated(true);
      newValue = newValue.slice(0, maxLength);
    } else {
      setIsTruncated(false);
    }
    
    e.target.value = newValue;

    if (onChange) {
      onChange(newValue);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Only for number type inputs
    if (type === "number") {
      // Allow numeric keys and control keys
      if (
        !/[\d]/.test(e.key) &&
        ![
          "Backspace",
          "Delete",
          "ArrowLeft",
          "ArrowRight",
          "Tab",
          "Enter",
        ].includes(e.key)
      ) {
        e.preventDefault();
      }

      // Check if we've reached maxLength and not pressing control keys
      const input = e.currentTarget;
      if (
        maxLength &&
        input.value.length >= maxLength &&
        // If text is selected, we should allow typing (it will replace the selection)
        input.selectionStart === input.selectionEnd &&
        ![
          "Backspace",
          "Delete",
          "ArrowLeft",
          "ArrowRight",
          "Tab",
          "Enter",
        ].includes(e.key)
      ) {
        e.preventDefault();
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
  };

  const handleCopy = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
  };

  const registerWithCustomHandlers = {
    ...register(name),
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      handleInputChange(e);
      register(name).onChange(e);
    },
    onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
      register(name).onBlur(e);
      if (onBlur) onBlur(e);
    },
  };

  return (
    <div
      className={`input-container ${className}`}
      style={{
        width: "100%",
        marginBottom: "1rem",
        position: "relative",
      }}
    >
      <div className="flex items-center gap-1 mb-1">
        <label
          htmlFor={inputId}
          className="text-[14px] font-[600] tracking-[1%]"
          style={{ display: "block", marginBottom: "0rem" }}
        >
          {label}
        </label>
        <div>
          {verified && (
            <div className="flex gap-1 items-center">
              <SecureImage
                fileKey={verificationbadge}
                alt="header icon"
                width="20px"
              />
              <CustomChip
                text={"Verified"}
                textColor="#04D900"
                backgroundColor="#ECFFEB"
                borderColor="#04D900"
              />
            </div>
          )}
        </div>
      </div>

      <div className="relative">
        <input
          id={inputId}
          name={name}
          type={type === "password" && !showPassword ? "password" : "text"}
          value={isTruncated ? `${value.slice(0, maxLength)}...` : value || ""}
          placeholder={placeholder}
          autoComplete={autoComplete}
          disabled={disabled}
          maxLength={maxLength}
          title={title}
          pattern={type === "number" ? "[0-9]*" : undefined}
          inputMode={type === "number" ? "numeric" : undefined}
          onKeyPress={handleKeyPress}
          // onPaste={handlePaste}
          onCopy={handleCopy}
          className="placeholder-[#8897AD] h-[42.8px] font-[400] leading-[20.64px] text-[#333] truncate"
          style={{
            width: "100%",
            padding: "12px 16px 12px 20px", 
            borderRadius: "15.48px",
            border: `1px solid ${errors[name] ? "#EF4444" : "#D1D5DB"}`,
            outline: "none",
            fontSize: "14px",
            lineHeight: "1.25rem",
            backgroundColor: "#F7FBFF",
            letterSpacing: "1%",
            textOverflow: "ellipsis",
            overflow: "hidden",
            whiteSpace: "nowrap",
          }}
          {...registerWithCustomHandlers}
        />
        {type === "password" && (
          <div
            style={{
              position: "absolute",
              top: "50%",
              right: "12px",
              transform: "translateY(-50%)",
              cursor: "pointer",
              backgroundColor: "#F7FBFF",
              paddingLeft: "5px",
            }}
            onMouseEnter={() => setShowPassword(true)}
            onMouseLeave={() => setShowPassword(false)}
          >
            {showPassword ? (
              <SecureImage
                fileKey={eyeopened}
                alt="header icon"
                width="18px"
              />
            ) : (
              <SecureImage
                fileKey={eyeclosed}
                alt="header icon"
                width="18px"
              />
            )}
          </div>
        )}
      </div>

      {errors[name] && (
        <p style={{ marginTop: "0.5rem", fontSize: "14px", color: "#EF4444" }}>
          {errors[name]?.message}
        </p>
      )}
    </div>
  );
};

export default AuthInputText;